import { useCallback, useRef, useState } from 'react';
import api from '../api/client';
import { displayUnit, isVolumeUnit } from '../components/service-order/types';

// Общие каталоги 1С для обоих видов заказ-наряда (план 2026-09-07-oil-change-unification, Task 3).
// Схема: OilChangePage.md §1 п.6 (rev. 2026-09-08) — услуги/запчасти из каталога 1С с GUID,
// иначе в 1С строка уходит без наименования.

export interface CatalogOption {
  value: string;                 // GUID номенклатуры в 1С
  label: React.ReactNode;        // подпись в селекторе
  name: string;                  // имя 1С (уходит в позицию)
  displayName?: string;          // имя из каталога товаров (для показа в селекторах)
  brand?: string | null;         // бренд из каталога (для сортировки/группировки)
  price: number;
  unit: string;
  article?: string | null;
  available?: number | null;
  markingExpected?: boolean;     // «ожидаемо маркируемая» позиция (marking_piece §2.1)
  types?: string[];              // типы товара из каталога (для фильтров, напр. «масло моторное»)
  catalogId?: number | null;     // карточка каталога (правая колонка фасовок, OilChangePage §3Б)
  priceFromPriceList?: boolean;  // цена из клиентского прайса (OilChangePage rev. 2026-09-12 №19)
  _search?: string;              // нижний регистр для локального фильтра
}

export const redLast = (opts: CatalogOption[]): CatalogOption[] => [
  ...opts.filter(o => !o.markingExpected),
  ...opts.filter(o => o.markingExpected),
];

// Авто-услуги замены масла — две реальные услуги 1С (OilChangePage.md §1 п.5).
// Общие константы обоих видов (rev. 2026-09-19 №40): в селекторах услуг первыми —
// бесплатная, затем платная (autoServiceFirst)
export const SERVICE_NAME_PAID = 'Масло в ДВС - замена';
export const SERVICE_NAME_FREE = 'Масло в ДВС (Mobil) - бесплатная замена';
export const normServiceName = (s?: string | null) => (s || '').trim();
export const isAutoServiceName = (n?: string | null) =>
  normServiceName(n) === SERVICE_NAME_PAID || normServiceName(n) === SERVICE_NAME_FREE;
export const autoServiceFirst = <T extends { name: string }>(opts: T[]): T[] => [
  ...opts.filter(o => normServiceName(o.name) === SERVICE_NAME_FREE),
  ...opts.filter(o => normServiceName(o.name) === SERVICE_NAME_PAID),
  ...opts.filter(o => !isAutoServiceName(o.name)),
];

// Сортировка «фильтры первыми» в селекторах запчастей (ServiceOrderPage §2Б /
// OilChangePage §1 п.8, rev. 2026-09-15): внутри группы первыми фильтры в порядке
// масляный → воздушный → салонный → топливный (по имени), затем ЛЮБОЙ другой
// фильтр («Фильтр АКПП», «Фильтр охладителя CVT», опечатки «Филтр»/«оладителя»),
// затем остальные; красные (markingExpected) — всегда в конце. Сортировка стабильная.
// NB: \w в JS не матчит кириллицу — словные разрывы только через [а-яё]* (№30).
const FILTER_PATTERNS: RegExp[] = [
  /фильтр\s*маслян|маслян[а-яё]*\s*фильтр/i,
  /фильтр\s*воздушн|воздушн[а-яё]*\s*фильтр/i,
  /фильтр\s*салон|салонн[а-яё]*\s*фильтр/i,
  /фильтр\s*топливн|топливн[а-яё]*\s*фильтр/i,
];
const ANY_FILTER_RE = /фил[а-яё]*тр/i;
const partOptionRank = (o: CatalogOption): number => {
  if (o.markingExpected) return 100;
  const n = (o.name || '').toLowerCase();
  const i = FILTER_PATTERNS.findIndex(re => re.test(n));
  if (i !== -1) return i;
  return ANY_FILTER_RE.test(n) ? FILTER_PATTERNS.length : 10;
};
export const filtersFirst = (opts: CatalogOption[]): CatalogOption[] =>
  [...opts].sort((a, b) => partOptionRank(a) - partOptionRank(b));

// Перевод раскладки QWERTY↔ЙЦУКЕН (та же клавиша) — поиск при забытой
// раскладке: «ьщишд» → «mobil», «vfckj» → «масло» (ServiceOrderPage №26)
const LAYOUT_PAIRS: [string, string][] = [
  ['й', 'q'], ['ц', 'w'], ['у', 'e'], ['к', 'r'], ['е', 't'], ['н', 'y'],
  ['г', 'u'], ['ш', 'i'], ['щ', 'o'], ['з', 'p'], ['х', '['], ['ъ', ']'],
  ['ф', 'a'], ['ы', 's'], ['в', 'd'], ['а', 'f'], ['п', 'g'], ['р', 'h'],
  ['о', 'j'], ['л', 'k'], ['д', 'l'], ['ж', ';'], ['э', "'"],
  ['я', 'z'], ['ч', 'x'], ['с', 'c'], ['м', 'v'], ['и', 'b'], ['т', 'n'],
  ['ь', 'm'], ['б', ','], ['ю', '.'],
];
const LAYOUT_MAP: Record<string, string> = {};
LAYOUT_PAIRS.forEach(([ru, en]) => { LAYOUT_MAP[ru] = en; LAYOUT_MAP[en] = ru; });
export const swapLayout = (s: string): string =>
  s.split('').map(ch => LAYOUT_MAP[ch] ?? ch).join('');

// Локальный фильтр опций склада по введённому тексту (слова через пробел).
// Каждое слово матчится в оригинале и в переводе раскладки (rev. №26).
export const filterWarehouseOptions = (opts: CatalogOption[], q: string): CatalogOption[] => {
  const query = q.trim().toLowerCase();
  if (!query) return opts;
  const words = query.split(/\s+/);
  return opts.filter(o => words.every(w => {
    const text = o._search || '';
    if (text.includes(w)) return true;
    const swapped = swapLayout(w);
    return swapped !== w && text.includes(swapped);
  }));
};

export const useOneCCatalogs = () => {
  const [serviceOptions, setServiceOptions] = useState<CatalogOption[]>([]);
  const [productOptions, setProductOptions] = useState<CatalogOption[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<CatalogOption[]>([]);

  // NOTE: /api/1c/catalog возвращает массив напрямую (не {items: [...]}),
  // поэтому читаем r.data как массив с fallback на r.data.items
  const loadServices = useCallback(async (q?: string) => {
    try {
      const r = await api.get('/1c/catalog', { params: { type: 'service', search: q } });
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setServiceOptions(items
        // Помеченные на удаление в 1С (имя с «*», напр. «*Масло в ДВС - замена») — скрыты
        // из подбора в обоих видах (rev. 2026-09-19, ServiceOrderPage №13 / OilChangePage №40)
        .filter((s: any) => !(s.name || '').trim().startsWith('*'))
        .map((s: any) => ({
          value: s.id,
          label: s.name,
          name: s.name,
          price: s.price != null ? Number(s.price) : 0,
          unit: s.unit || 'шт',
        })));
    } catch { /* каталог недоступен — группа останется пустой */ }
  }, []);

  // Товары 1С с барьерами маркировки (marking_piece §2.1, схема ServiceOrderPage §2Б):
  // exclude_marked_piece — штучные маркируемые скрыты (только сканом марки);
  // exclude_liquids — жидкости скрыты (подбираются из группы «Наш склад»)
  const loadProducts = useCallback(async (q?: string) => {
    try {
      const r = await api.get('/1c/catalog', {
        params: { type: 'product', search: q, exclude_marked_piece: true, exclude_liquids: true },
      });
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setProductOptions(items.map((s: any) => ({
        value: s.id,
        label: `${s.article ? `${s.article} — ` : ''}${s.name}${s.stock ? ` - ${+s.stock.available.toFixed(1)} ${displayUnit(s.unit_name) || 'шт'}.` : ''}`,
        name: s.name,
        price: s.price != null ? Number(s.price) : 0,
        unit: displayUnit(s.unit || s.unit_name) || 'шт',
        article: s.article ?? null,
        available: s.stock ? +s.stock.available : null,
        markingExpected: !!s.marking_expected,
        priceFromPriceList: !!s.price_from_price_list,
      })));
    } catch { /* ignore */ }
  }, []);

  // Группа «Наш склад» (rev. 2026-08-26): жидкости из снимка склада.
  // Один запрос — дальше фильтр локальный (filterWarehouseOptions).
  const loadWarehouse = useCallback(async () => {
    try {
      const r = await api.get('/warehouse/liquids');
      const rows = r.data?.rows || [];
      const opts: CatalogOption[] = [];
      rows.forEach((row: any) => {
        if (!row.is_liquid) return;
        const push = (o: any) => {
          const unit = o.unit_name ? displayUnit(o.unit_name) : (o.volume != null ? 'шт' : 'л');
          // Штучные маркируемые (бутылки с ЧЗ) — только сканом марки (marking_piece §2.1)
          if (o.is_marked && !isVolumeUnit(unit)) return;
          // Старые дубли-карточки с мусорным маркером в имени — мусор (warehouse_liquids rev. 5/13)
          if (/\(ml\)|\(мл\)|ошибка|не использовать/i.test(o.item_name || '')) return;
          const avail = o.available;
          const base = [row.display_name, o.package_label].filter(Boolean).join(' · ');
          opts.push({
            value: o.item_id,
            label: `${o.article ? `${o.article} — ` : ''}${base}${avail != null ? ` - ${+avail.toFixed(1)} ${unit}.` : ''}`,
            name: o.item_name,
            displayName: base,
            brand: row.brand ?? null,
            price: o.sale_price ?? o.purchase_price ?? 0,
            // sale_price у склада заполняется только из клиентского прайса
            // (warehouse.py _packages_for) — признак «цена из прайса»
            priceFromPriceList: o.sale_price != null,
            unit,
            article: o.article ?? null,
            available: avail != null ? Number(avail) : null,
            markingExpected: !!o.marking_expected,
            types: (row.product_types || []).map((t: any) => String(t)),
            catalogId: row.catalog_id ?? null,
            _search: `${row.display_name} ${o.item_name} ${o.article ?? ''}`.toLowerCase(),
          });
        };
        if (row.packages && row.packages.length) {
          row.packages.forEach(push);
        } else {
          // Неслинкованная жидкость — одиночная опция по строке снимка
          push({
            item_id: row.item_id,
            item_name: row.item_name,
            package_label: null,
            volume: null,
            unit_name: row.unit_name,
            article: row.article,
            sale_price: row.sale_price,
            purchase_price: row.purchase_price,
            available: row.available,
            is_marked: row.is_marked,
          });
        }
      });
      setWarehouseOptions(opts);
    } catch {
      // Склад необязателен для подбора — группа просто останется пустой
    }
  }, []);

  // Автомобили больше не ищем в 1С — справочник AR_DB (VehiclePicker, vehicle_resolver.md)

  // Точный поиск услуги в каталоге 1С по имени → GUID (авто-услуга, быстрые кнопки).
  // Сравнение без регистра и хвостовых пробелов (в 1С имена с пробелами на конце)
  const findServiceGuid = useCallback(async (name: string): Promise<{ id: string; price: number } | null> => {
    try {
      const r = await api.get('/1c/catalog', { params: { type: 'service', search: name } });
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      const norm = (s?: string | null) => (s || '').trim().toLowerCase();
      const exact = items.find((s: any) => norm(s.name) === norm(name));
      return exact ? { id: exact.id, price: exact.price != null ? Number(exact.price) : 0 } : null;
    } catch {
      return null;
    }
  }, []);

  // Debounce 300 мс — не дёргать API на каждое нажатие
  const searchTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const debouncedSearch = useCallback((key: string, fn: (q?: string) => void, q?: string) => {
    if (searchTimers.current[key]) clearTimeout(searchTimers.current[key]);
    searchTimers.current[key] = setTimeout(() => fn(q), 300);
  }, []);
  const debouncedLoadServices = useCallback(
    (q?: string) => debouncedSearch('services', loadServices, q), [debouncedSearch, loadServices]);
  const debouncedLoadProducts = useCallback(
    (q?: string) => debouncedSearch('products', loadProducts, q), [debouncedSearch, loadProducts]);

  return {
    serviceOptions, productOptions, warehouseOptions,
    loadServices, loadProducts, loadWarehouse,
    debouncedLoadServices, debouncedLoadProducts,
    findServiceGuid,
  };
};
